import { OktaAuth } from "@okta/okta-auth-js";
import { useEffect, useRef } from "react";

import { useUserStore } from "../stores/userStore";
import { AUTH_BASE_URL } from "../utils/env.config";

type IdleOptions = {
  idleMs?: number;
  warnMs?: number;
  onWarn?: (remainingMs: number) => void;
  events?: (keyof DocumentEventMap)[];
};

export function useIdleLogout(
  oktaAuth: OktaAuth,
  {
    idleMs = 15 * 60 * 1000,
    warnMs,
    onWarn,
    events = ["pointerdown", "click", "keydown", "wheel"],
  }: IdleOptions = {},
) {
  const [userState, clearUserState] = useUserStore(state => [
    state.user,
    state.clearUser,
  ]);

  const logoutTimerRef = useRef<number | null>(null);
  const warnTimerRef = useRef<number | null>(null);
  const bcRef = useRef<BroadcastChannel | null>(null);
  const bcClosedRef = useRef<boolean>(false);
  const onWarnRef = useRef(onWarn);
  onWarnRef.current = onWarn;

  useEffect(() => {
    const channelName = "app_idle_channel";
    const bc =
      typeof BroadcastChannel !== "undefined"
        ? new BroadcastChannel(channelName)
        : null;
    bcRef.current = bc;
    bcClosedRef.current = false;

    const LAST_ACTIVE_KEY = "app_last_active_ts";

    const clearTimers = () => {
      if (logoutTimerRef.current != null) {
        window.clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      if (warnTimerRef.current != null) {
        window.clearTimeout(warnTimerRef.current);
        warnTimerRef.current = null;
      }
    };

    const safePost = (msg: any) => {
      const ch = bcRef.current;
      if (!ch || bcClosedRef.current) return;
      try {
        ch.postMessage(msg);
      } catch (e) {
        console.warn("[idle] broadcast failed", e);
      }
    };

    let lastActiveBroadcast = 0;
    const ACTIVE_BROADCAST_INTERVAL_MS = 5000;

    const markActive = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      localStorage.setItem(LAST_ACTIVE_KEY, String(now));
      if (now - lastActiveBroadcast >= ACTIVE_BROADCAST_INTERVAL_MS) {
        safePost({ type: "active", at: now });
        lastActiveBroadcast = now;
      }
      resetTimers();
    };

    const doLogout = async () => {
      const last = Number(localStorage.getItem(LAST_ACTIVE_KEY) || "0");
      const elapsed = Date.now() - last;
      if (elapsed >= idleMs) {
        try {
          await oktaAuth.signOut({
            clearTokensBeforeRedirect: true,
            postLogoutRedirectUri: AUTH_BASE_URL,
          });
          clearUserState();
          safePost({ type: "logout" });
        } catch (e) {
          console.error("[idle] signOut failed", e);
        }
      } else {
        resetTimers();
      }
    };

    const resetTimers = () => {
      clearTimers();
      const last = Number(localStorage.getItem(LAST_ACTIVE_KEY) || "0");
      const elapsed = Date.now() - last;

      if (warnMs && onWarnRef.current && warnMs < idleMs) {
        const remaining = idleMs - elapsed;
        const warnDelay = remaining - warnMs;
        
        if (warnDelay > 0) {
          warnTimerRef.current = window.setTimeout(() => {
            const lastInner = Number(
              localStorage.getItem(LAST_ACTIVE_KEY) || "0",
            );
            const elapsedInner = Date.now() - lastInner;
            const remainingInner = idleMs - elapsedInner;
            
            if (remainingInner > 0) onWarnRef.current?.(remainingInner);
          }, warnDelay);
        }
      }

      const logoutDelay = Math.max(0, idleMs - elapsed);
      
      logoutTimerRef.current = window.setTimeout(() => {
        doLogout();
      }, logoutDelay);
    };

    const checkAndMaybeLogout = () => {
      const last = Number(localStorage.getItem(LAST_ACTIVE_KEY) || "0");
      const elapsed = Date.now() - last;

      if (elapsed >= idleMs) {
        void doLogout();
      }
    };

    const onMessage = (ev: MessageEvent) => {
      const data = ev?.data;
      if (!data) return;
      if (data.type === "active") {
        const last = Number(localStorage.getItem(LAST_ACTIVE_KEY) || "0");
        const elapsed = Date.now() - last;
        if (document.visibilityState === "visible" && elapsed < idleMs) {
          resetTimers();
        }
      } else if (data.type === "logout") {
        oktaAuth
          .signOut({
            clearTokensBeforeRedirect: true,
            postLogoutRedirectUri: AUTH_BASE_URL,
          })
          .catch(e => console.error("[idle] signOut (bc) failed", e));
        clearUserState();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkAndMaybeLogout();
        
        const last = Number(localStorage.getItem(LAST_ACTIVE_KEY) || "0");
        if (Date.now() - last < idleMs) markActive();
      }
    };

    if (!localStorage.getItem(LAST_ACTIVE_KEY)) {
      localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
    }
    resetTimers();

    events.forEach(evt => {
      document.addEventListener(evt, markActive, {
        passive: true,
        capture: true,
      });
    });
    document.addEventListener("visibilitychange", handleVisibility, {
      passive: true,
    });
    window.addEventListener("focus", checkAndMaybeLogout, { passive: true });

    bc?.addEventListener("message", onMessage as any);

    return () => {
      events.forEach(evt => {
        document.removeEventListener(evt, markActive as any);
      });
      document.removeEventListener("visibilitychange", handleVisibility as any);
      window.removeEventListener("focus", checkAndMaybeLogout as any);
      bc?.removeEventListener("message", onMessage as any);
      bcClosedRef.current = true;
      bc?.close();
      bcRef.current = null;
      clearTimers();
    };
  }, [oktaAuth, idleMs, warnMs]);
}
